import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, first, switchMap, tap } from 'rxjs';
import { Microsoft360ViewResource } from '../resources/microsoft-360-view.resource';
import { Microsoft360ViewPollingService } from '../services/microsoft/microsoft-360-view-polling.service';
import { ActivatedRoute } from '@angular/router';
import { Microsoft360ViewContext } from './microsoft-360-view.context';
import { MicrosoftTenantsStateContext } from './microsoft-tenants-state.context';
import { take } from 'rxjs/operators';

@Injectable()
export class UsageLocationContext {
  private _microsoft360ViewResource = inject(Microsoft360ViewResource);
  private _ms360ViewPollingService = inject(Microsoft360ViewPollingService);
  private _microsoft360ViewContext = inject(Microsoft360ViewContext);
  private _route = inject(ActivatedRoute);
  private _tenantsStateContext = inject(MicrosoftTenantsStateContext);

  public _stateSource = new BehaviorSubject<boolean>(false);
  public state$ = this._stateSource.asObservable();
  private _tenantId: string;

  constructor() {
    const upn = this._route.snapshot.paramMap.get('upn');
    this._tenantsStateContext.cachedTenantState$
      .pipe(
        filter((cachedTenant) => !!cachedTenant?.tenantId),
        take(1),
      )
      .subscribe((cachedTenant) => {
        this._tenantId = cachedTenant.tenantId.toString();
        this._refresh(upn);
      });
  }

  private _refresh(upn: string): void {
    this._microsoft360ViewResource
      .get(upn, this._tenantId)
      .subscribe((ms360View) => this._stateSource.next(ms360View.licensingStatus.usageLocationUpdateInProgress));
  }

  startPolling(upn: string): void {
    if (this._stateSource.value) {
      this.stopPolling();
    }
    this._stateSource.next(true);
    this._ms360ViewPollingService.setBaseUrl(upn, this._tenantId);
    this._ms360ViewPollingService.startPolling();
    this._ms360ViewPollingService.state$
      .pipe(
        first((microsoft360View) => !microsoft360View.licensingStatus.usageLocationUpdateInProgress),
        tap(() => this._stateSource.next(false)),
        switchMap(() => this._microsoft360ViewContext.refresh(upn)),
      )
      .subscribe(() => this.stopPolling());
  }

  stopPolling(): void {
    this._ms360ViewPollingService.stopPolling();
    this._stateSource.next(false);
  }
}
